import React, { useState, useEffect } from 'react';
import { api } from '../lib/api';

interface VerticalPack {
  industry: string;
  name: string;
  description: string;
  template_count: number;
  sequence_count: number;
}

interface PackPreview {
  name: string;
  description: string;
  templates: Array<{
    name: string;
    description: string;
    type: string;
    subject?: string;
  }>;
  sequences: Array<{
    name: string;
    description: string;
    step_count: number;
  }>;
  landing_page_copy: {
    high_rating_title?: string;
    high_rating_message?: string;
    low_rating_title?: string;
    low_rating_message?: string;
  };
}

interface VerticalPackSelectorProps {
  onSelect: (industry: string) => void;
  selectedIndustry?: string;
  showPreview?: boolean;
}

export const VerticalPackSelector: React.FC<VerticalPackSelectorProps> = ({
  onSelect,
  selectedIndustry = 'general',
  showPreview = true,
}) => {
  const [packs, setPacks] = useState<VerticalPack[]>([]);
  const [preview, setPreview] = useState<PackPreview | null>(null);
  const [loading, setLoading] = useState(true);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadPacks();
  }, []);

  useEffect(() => {
    if (selectedIndustry && selectedIndustry !== 'general' && showPreview) {
      loadPreview(selectedIndustry);
    } else {
      setPreview(null);
    }
  }, [selectedIndustry, showPreview]);

  const loadPacks = async () => {
    try {
      setLoading(true);
      const response = await api.verticalPacks.list();
      setPacks(response.data.packs || []);
    } catch (err: any) {
      console.error('Failed to load vertical packs:', err);
      setError('Failed to load vertical packs');
    } finally {
      setLoading(false);
    }
  };

  const loadPreview = async (industry: string) => {
    try {
      setPreviewLoading(true);
      const response = await api.verticalPacks.get(industry);
      setPreview(response.data);
    } catch (err: any) {
      console.error('Failed to load pack preview:', err);
    } finally {
      setPreviewLoading(false);
    }
  };

  const handlePackSelect = (industry: string) => {
    onSelect(industry);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <p className="text-red-800">{error}</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-4">
          Select Your Industry
        </h3>
        <p className="text-sm text-gray-600 mb-4">
          Choose an industry pack to get pre-configured templates, sequences, and import mappings
          tailored to your business.
        </p>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* General/Custom Option */}
          <button
            onClick={() => handlePackSelect('general')}
            className={`p-4 border-2 rounded-lg text-left transition-all ${
              selectedIndustry === 'general'
                ? 'border-blue-500 bg-blue-50'
                : 'border-gray-200 hover:border-gray-300'
            }`}
          >
            <div className="flex items-center justify-between mb-2">
              <h4 className="font-semibold text-gray-900">General / Custom</h4>
              {selectedIndustry === 'general' && (
                <svg
                  className="w-5 h-5 text-blue-600"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                    clipRule="evenodd"
                  />
                </svg>
              )}
            </div>
            <p className="text-sm text-gray-600">
              Start with a blank slate and customize everything yourself
            </p>
          </button>

          {/* Industry-Specific Packs */}
          {packs.map((pack) => (
            <button
              key={pack.industry}
              onClick={() => handlePackSelect(pack.industry)}
              className={`p-4 border-2 rounded-lg text-left transition-all ${
                selectedIndustry === pack.industry
                  ? 'border-blue-500 bg-blue-50'
                  : 'border-gray-200 hover:border-gray-300'
              }`}
            >
              <div className="flex items-center justify-between mb-2">
                <h4 className="font-semibold text-gray-900">{pack.name}</h4>
                {selectedIndustry === pack.industry && (
                  <svg
                    className="w-5 h-5 text-blue-600"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                      clipRule="evenodd"
                    />
                  </svg>
                )}
              </div>
              <p className="text-sm text-gray-600 mb-3">{pack.description}</p>
              <div className="flex items-center space-x-4 text-xs text-gray-500">
                <span>{pack.template_count} templates</span>
                <span>{pack.sequence_count} sequences</span>
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Preview Section */}
      {showPreview && selectedIndustry !== 'general' && (
        <div className="border-t pt-6">
          {previewLoading ? (
            <div className="flex justify-center py-4">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
            </div>
          ) : preview ? (
            <div className="space-y-4">
              <h4 className="font-semibold text-gray-900">Pack Preview</h4>

              {/* Templates Preview */}
              {preview.templates && preview.templates.length > 0 && (
                <div>
                  <h5 className="text-sm font-medium text-gray-700 mb-2">
                    Templates ({preview.templates.length})
                  </h5>
                  <div className="space-y-2">
                    {preview.templates.slice(0, 3).map((template, idx) => (
                      <div
                        key={idx}
                        className="bg-gray-50 rounded-lg p-3 text-sm"
                      >
                        <div className="flex items-start justify-between">
                          <div>
                            <p className="font-medium text-gray-900">
                              {template.name}
                            </p>
                            <p className="text-gray-600">{template.description}</p>
                            {template.subject && (
                              <p className="text-xs text-gray-500 mt-1">
                                Subject: {template.subject}
                              </p>
                            )}
                          </div>
                          <span
                            className={`px-2 py-1 text-xs rounded ${
                              template.type === 'email'
                                ? 'bg-blue-100 text-blue-700'
                                : 'bg-green-100 text-green-700'
                            }`}
                          >
                            {template.type.toUpperCase()}
                          </span>
                        </div>
                      </div>
                    ))}
                    {preview.templates.length > 3 && (
                      <p className="text-sm text-gray-500 text-center">
                        +{preview.templates.length - 3} more templates
                      </p>
                    )}
                  </div>
                </div>
              )}

              {/* Sequences Preview */}
              {preview.sequences && preview.sequences.length > 0 && (
                <div>
                  <h5 className="text-sm font-medium text-gray-700 mb-2">
                    Sequences ({preview.sequences.length})
                  </h5>
                  <div className="space-y-2">
                    {preview.sequences.map((sequence, idx) => (
                      <div
                        key={idx}
                        className="bg-gray-50 rounded-lg p-3 text-sm"
                      >
                        <p className="font-medium text-gray-900">{sequence.name}</p>
                        <p className="text-gray-600">{sequence.description}</p>
                        <p className="text-xs text-gray-500 mt-1">
                          {sequence.step_count} steps
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Landing Page Copy Preview */}
              {preview.landing_page_copy && (
                <div>
                  <h5 className="text-sm font-medium text-gray-700 mb-2">
                    Landing Page Copy
                  </h5>
                  <div className="bg-gray-50 rounded-lg p-3 text-sm space-y-2">
                    {preview.landing_page_copy.high_rating_message && (
                      <div>
                        <p className="font-medium text-gray-900">High Rating:</p>
                        <p className="text-gray-600">
                          {preview.landing_page_copy.high_rating_message}
                        </p>
                      </div>
                    )}
                    {preview.landing_page_copy.low_rating_message && (
                      <div>
                        <p className="font-medium text-gray-900">Low Rating:</p>
                        <p className="text-gray-600">
                          {preview.landing_page_copy.low_rating_message}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          ) : null}
        </div>
      )}
    </div>
  );
};

export default VerticalPackSelector;
